import { createEcoShapesContainerNode, createFabShapesContainerNode, createMountsContainerNode, createNodeRuleSet, createPadNodeUnderMountsContainer, createShapesContainerNode, createSilkShapesContainerNode, createTextShapeUnderShapesContainer, FootPrintPadShape, FootPrintShapeType, } from "@buildwithflux/models";
import { metersToMm, radiansToDegrees } from "@buildwithflux/shared";
import { isEmpty } from "lodash";
import { uuid } from "short-uuid";
import { packages } from "../../../../../parametric_footprints/packages";
import { vectorsToSvgPath } from "../../../../../shapes";
import { KicadImportTextPlaceholder, TextPlaceholder } from "../../../../../SharedDataModels";
import { FontStyle, FootPrintPadHoleType, PcbLayoutFootprintPadShape, TextAlignment, } from "../../../../rules";
import { getAssetByName } from "../../../helpers";
import { PcbLayoutRuleCompiler } from "../../../rule_parsing_and_compilation";
import { getMatchingPadNode } from "./helpers/getMatchingPadNode";
import { parseFootprintData } from "./helpers/parseFootprintData";
import { arcShapeToSvgPath, circleShapeToSvgPath, lineShapeToSvgPath, polygonShapeToSvgPath, } from "./helpers/shapeToSvgPath";
export default class FootprintVirtualNodeGenerator {
    logger;
    constructor(logger) {
        this.logger = logger;
    }
    static assetPadShapeToFluxPadShape(pad) {
        switch (pad.shape) {
            case FootPrintPadShape.circular: {
                return PcbLayoutFootprintPadShape.circular;
            }
            case FootPrintPadShape.ellipse: {
                return PcbLayoutFootprintPadShape.circular;
            }
            case FootPrintPadShape.rectangle: {
                return PcbLayoutFootprintPadShape.rectangle;
            }
            case FootPrintPadShape.polygon: {
                return PcbLayoutFootprintPadShape.polygon;
            }
            case FootPrintPadShape.obround: {
                return PcbLayoutFootprintPadShape.rectangle;
            }
            case FootPrintPadShape.trapezoid: {
                return PcbLayoutFootprintPadShape.rectangle;
            }
        }
    }
    generate(footprintNodeUid, bakedRules, footprintAncestorElementData, footprintAsset, documentElements, existingPadNodes, isInSubmodule, kicadFootprintToFluxFootprint) {
        const result = { virtualNodes: [], virtualRuleSets: {} };
        // Assets footprints take priority over parametric footprints
        if (bakedRules.asset) {
            // The `shapesContainerNode` always have the same uid given a `footprintNodeUid` as its parent
            const shapesContainerNode = createShapesContainerNode(footprintNodeUid);
            // The `mountsContainerNode` always have the same uid given a `footprintNodeUid` as its parent
            const mountsContainerNode = createMountsContainerNode(footprintNodeUid);
            const generatedRules = this.applyFootprintAssetRule(bakedRules.asset, footprintNodeUid, shapesContainerNode.uid, mountsContainerNode.uid, footprintAncestorElementData, footprintAsset, documentElements, existingPadNodes, isInSubmodule, kicadFootprintToFluxFootprint);
            if (generatedRules) {
                if (!isEmpty(generatedRules.shapeNodes)) {
                    result.virtualNodes.push(shapesContainerNode);
                    // eslint-disable-next-line @buildwithflux/no-array-foreach
                    Object.values(generatedRules.shapeNodes).forEach((shapeNode) => {
                        result.virtualNodes.push(shapeNode);
                    });
                }
                if (!isEmpty(generatedRules.mountPadNodes)) {
                    result.virtualNodes.push(mountsContainerNode);
                    // eslint-disable-next-line @buildwithflux/no-array-foreach
                    Object.values(generatedRules.mountPadNodes).forEach((padNode) => {
                        result.virtualNodes.push(padNode);
                    });
                }
                // eslint-disable-next-line @buildwithflux/no-array-foreach
                Object.values(generatedRules.rules).forEach((rule) => {
                    result.virtualRuleSets[rule.uid] = rule;
                });
                return result;
            }
        }
        const parametricGenerator = bakedRules.package ? packages[bakedRules.package] : null;
        if (parametricGenerator) {
            const padRules = parametricGenerator.generatePadRules(footprintNodeUid, bakedRules);
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            Object.values(padRules).forEach((rule) => {
                result.virtualRuleSets[rule.uid] = rule;
            });
            const additionalPads = parametricGenerator.generateAdditionalPads(footprintNodeUid, bakedRules);
            result.virtualNodes.push(...additionalPads);
            const silkNodes = parametricGenerator.generateSilkNodes(footprintNodeUid, bakedRules);
            result.virtualNodes.push(...silkNodes);
            const modelNodes = parametricGenerator.generate3DModelNode(footprintNodeUid, bakedRules);
            result.virtualNodes.push(...modelNodes);
            return result;
        }
    }
    applyFootprintAssetRule(footprintAssetRule, footprintNodeUid, shapesContainerNodeUid, mountsContainerNodeUid, footprintAncestorElementData, footprintAsset, documentElements, existingPadNodes, isInSubmodule, kicadFootprintToFluxFootprint) {
        if (!footprintAssetRule?.value) {
            return;
        }
        const assetData = getAssetByName(footprintAssetRule.value.toString(), footprintAsset ? { [footprintAsset.uid]: footprintAsset } : undefined, footprintAncestorElementData);
        let footprintData;
        try {
            //
            footprintData = parseFootprintData(footprintNodeUid, footprintAssetRule, assetData?.storageName, kicadFootprintToFluxFootprint);
        }
        catch (err) {
            this.logger.captureError(`Error parsing footprint data: ${err}`);
            return undefined;
        }
        if (footprintData) {
            const padBasedRulesAndMountingPads = this.createPadBasedRulesAndMountingPads(footprintData.pads, footprintNodeUid, mountsContainerNodeUid, documentElements, existingPadNodes, isInSubmodule);
            const shapeBasedNodes = this.createShapeBasedNodes(footprintNodeUid, footprintData.shapes, shapesContainerNodeUid, 
            // NOTE: this keyword should be added for all ultralibrarian
            // bulk imports in a preprocessing step
            footprintData.keywords
                ? footprintData.keywords.includes(KicadImportTextPlaceholder.ultralibrarianTag)
                : false);
            return {
                rules: padBasedRulesAndMountingPads.rules,
                shapeNodes: shapeBasedNodes.shapeNodes,
                mountPadNodes: padBasedRulesAndMountingPads.mountPadNodes,
            };
        }
        return undefined;
    }
    createShapeBasedNodes(footprintNodeUid, shapes, shapesContainerNodeUid, isFromUltraLibrarian) {
        const generatedData = { rules: {}, shapeNodes: {}, mountPadNodes: {} };
        const silkPaths = [];
        const ecoPaths = [];
        const fabPaths = [];
        const [topY, bottomY] = this.getTopAndBottomYs(shapes);
        shapes
            .filter((shape) => shape.type !== FootPrintShapeType.model)
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            .forEach((shape) => {
            const shapeUid = `${shapesContainerNodeUid}_${shape.uid}`;
            if ("layerUid" in shape) {
                const layer = this.getLayerType(shape.layerUid);
                // TODO: add support for all kicad shapes
                switch (shape.type) {
                    case FootPrintShapeType.text: {
                        if (shape.text) {
                            const textNode = createTextShapeUnderShapesContainer(shape.text, shapesContainerNodeUid, shapeUid, footprintNodeUid);
                            let contentRule = {};
                            if (shape.text === KicadImportTextPlaceholder.valueReplace) {
                                contentRule = { content: `= ${TextPlaceholder.elementDefaultValue}` };
                            }
                            else if (shape.text === KicadImportTextPlaceholder.refReplace) {
                                // NOTE: this shape should also have shape.kicadTextType === "reference"
                                contentRule = { content: `= ${TextPlaceholder.elementDesignator}` };
                                // HACK: Ultralibrarian puts the "* REF *" text at
                                // (0,0), which is not useful, so we move it to the
                                // top of the footprint, as defined by the silk
                                // shapes.
                                if (isFromUltraLibrarian && this.isAtOrigin(shape)) {
                                    // Move the ref text to the top of the footprint
                                    shape.position.y = topY + shape.size.y * 2;
                                }
                            }
                            else if (isFromUltraLibrarian &&
                                shape.kicadTextType === "value" &&
                                this.isAtOrigin(shape)) {
                                // HACK: Ultralibrarian puts the footprint package
                                // name at (0,0), which is not useful, so we move it
                                // to the bottom of the footprint, as defined by the
                                // silk shapes.
                                shape.position.y = bottomY - shape.size.y * 2;
                            }
                            textNode.pcbNodeRuleSet = createNodeRuleSet({
                                position: `${metersToMm(shape.position.x)}mm ${metersToMm(shape.position.y)}mm`,
                                strokeWidth: `${metersToMm(shape.size.x)}mm`,
                                fontSize: `${metersToMm(shape.size.x)}mm`,
                                fontStyle: shape.italic ? FontStyle.italic : FontStyle.normal,
                                textAlign: TextAlignment.center,
                                active: shape.layerUid === "Cmts.User" ? false : undefined, // Hide KiCad comments layer
                                // NOTE: we use undefined and not true here to not force active to true, which would require the !important flag to override
                                ...contentRule,
                            }, true);
                            generatedData.shapeNodes[textNode.uid] = textNode;
                        }
                        break;
                    }
                    case FootPrintShapeType.line: {
                        if (layer === "silk") {
                            silkPaths.push(lineShapeToSvgPath(shape));
                        }
                        else if (layer === "fab") {
                            fabPaths.push(lineShapeToSvgPath(shape));
                        }
                        else {
                            ecoPaths.push(lineShapeToSvgPath(shape));
                        }
                        break;
                    }
                    case FootPrintShapeType.circle: {
                        if (layer === "silk") {
                            silkPaths.push(circleShapeToSvgPath(shape));
                        }
                        else if (layer === "fab") {
                            fabPaths.push(circleShapeToSvgPath(shape));
                        }
                        else {
                            ecoPaths.push(circleShapeToSvgPath(shape));
                        }
                        break;
                    }
                    case FootPrintShapeType.arc: {
                        if (layer === "silk") {
                            silkPaths.push(arcShapeToSvgPath(shape));
                        }
                        else if (layer === "fab") {
                            fabPaths.push(arcShapeToSvgPath(shape));
                        }
                        else {
                            ecoPaths.push(arcShapeToSvgPath(shape));
                        }
                        break;
                    }
                    case FootPrintShapeType.polygon: {
                        if (layer === "silk") {
                            silkPaths.push(polygonShapeToSvgPath(shape));
                        }
                        else if (layer === "fab") {
                            fabPaths.push(polygonShapeToSvgPath(shape));
                        }
                        else {
                            ecoPaths.push(polygonShapeToSvgPath(shape));
                        }
                        break;
                    }
                }
            }
        });
        if (silkPaths.length > 0) {
            const svgNode = createSilkShapesContainerNode(shapesContainerNodeUid, footprintNodeUid, silkPaths);
            generatedData.shapeNodes[svgNode.uid] = svgNode;
        }
        if (ecoPaths.length > 0) {
            const svgNode = createEcoShapesContainerNode(shapesContainerNodeUid, footprintNodeUid, ecoPaths);
            generatedData.shapeNodes[svgNode.uid] = svgNode;
        }
        if (fabPaths.length > 0) {
            const svgNode = createFabShapesContainerNode(shapesContainerNodeUid, footprintNodeUid, fabPaths);
            if (isFromUltraLibrarian) {
                svgNode.pcbNodeRuleSet.active = { uid: uuid().toString(), key: "active", value: false };
            }
            generatedData.shapeNodes[svgNode.uid] = svgNode;
        }
        return generatedData;
    }
    isAtOrigin(shape) {
        return shape.position.x === 0 && shape.position.y === 0;
    }
    getTopAndBottomYs(shapes) {
        const yPositions = this.getSilkShapeYPositions(shapes);
        if (yPositions.length) {
            return [Math.max(...yPositions), Math.min(...yPositions)];
        }
        return [0, 0];
    }
    getSilkShapeYPositions(shapes) {
        return shapes
            .filter((shape) => (shape.type === FootPrintShapeType.circle || shape.type === FootPrintShapeType.line) &&
            (("position" in shape && shape.position) || ("start" in shape && shape.start && shape.end)))
            .map((shape) => "position" in shape ? shape.position?.y : "start" in shape ? Math.max(shape.start.y, shape.end.y) : 0);
    }
    getLayerType(layerUid) {
        if (layerUid?.toLowerCase().includes("eco")) {
            return "eco";
        }
        if (layerUid?.toLowerCase().includes("fab")) {
            return "fab";
        }
        return "silk";
    }
    createPadBasedRulesAndMountingPads(pads, footprintNodeUid, mountsContainerNodeUid, documentElements, existingPadNodes, isInSubmodule) {
        const generatedData = { rules: {}, shapeNodes: {}, mountPadNodes: {} };
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        pads.forEach((pad) => {
            const footprintPadName = pad.name?.toString().trim();
            let matchingPadNode = getMatchingPadNode(footprintPadName, existingPadNodes, documentElements, isInSubmodule, this.logger);
            // if there is no matching schematic terminal OR the kicad pad is of type non plated hole and doesn't have a name set then we assume its a mounting pad
            if (!matchingPadNode ||
                (!pad.name &&
                    (pad.holeType === FootPrintPadHoleType.nonPlatedHole ||
                        pad.holeType === FootPrintPadHoleType.platedThroughHole))) {
                matchingPadNode = createPadNodeUnderMountsContainer(pad.uid, pad.name, mountsContainerNodeUid, footprintNodeUid);
                generatedData.mountPadNodes[matchingPadNode.uid] = matchingPadNode;
            }
            const combineSelector = PcbLayoutRuleCompiler.createUidAttributeSelector(matchingPadNode.type, matchingPadNode.uid);
            const ruleSet = {
                // NOTE: the ruleset ID and the node ID is assumed to be the same elsewhere.
                // See PcbSelectorMatcher.
                uid: matchingPadNode.uid,
                selector: combineSelector,
                rules: {},
                generatedFromAncestorNodeUid: footprintNodeUid,
            };
            generatedData.rules[ruleSet.uid] = ruleSet;
            const rules = {
                position: `${metersToMm(pad.position.x)}mm ${metersToMm(pad.position.y)}mm`,
                rotationZ: `${radiansToDegrees(pad.position.orientation ?? 0)}deg`,
                padShape: FootprintVirtualNodeGenerator.assetPadShapeToFluxPadShape(pad),
                padHoleType: pad.holeType,
            };
            if (pad.shape !== FootPrintPadShape.polygon) {
                rules.size = [pad.size.x, pad.size.y, 0].map((axis) => metersToMm(axis)).join(" ");
            }
            if (pad.shape !== FootPrintPadShape.obround && pad.holeSize) {
                rules.holeSize = `${pad.holeSize.x}m ${pad.holeSize.y}m`;
            }
            if (pad.shape === FootPrintPadShape.obround) {
                if (pad.slottedHoleSize) {
                    rules.holeSize = [pad.slottedHoleSize.x, pad.slottedHoleSize.y]
                        .map((axis) => metersToMm(axis))
                        .join(" ");
                }
                if (pad.cornerRadius) {
                    rules.cornerRadius = metersToMm(pad.cornerRadius);
                }
            }
            if (pad.shape === FootPrintPadShape.polygon && pad.vertices) {
                rules.padShape = vectorsToSvgPath(pad.vertices);
            }
            if (pad.holePosition) {
                rules.holePosition = [pad.holePosition.x, pad.holePosition.y].join(" ");
            }
            ruleSet.rules = createNodeRuleSet(rules);
        });
        return generatedData;
    }
}
//# sourceMappingURL=FootprintVirtualNodeGenerator.js.map